import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listTree, recordsContext, ownDocumentScopes } from './documentsStore';
import { useDocumentsUiStore, subscribeDocumentChanges } from './documentsUiStore';
import { documentAncestors } from '../../../utils/documentTreePath';

/**
 * One tree read serving two consumers of the open document's scope:
 *
 *  - `ancestors`: the document's ancestor NODES root-first (shared
 *    cycle-guarded walk with the command palette — utils/documentTreePath.js),
 *    for the editor header breadcrumbs.
 *  - `resolve(docId)`: live existence + title for ANY document in the scope,
 *    for the PageLinkContext provider (chips). Returns null while the tree
 *    hasn't loaded yet (chips fall back to their cached titles), then
 *    { exists, title }.
 *
 * Refreshes on the shared treeVersion nonce (renames, creates, deletes bump
 * it) AND on the WS fast path, filtered to this scope exactly the way the
 * sidebar filters it (documentsUiStore.subscribeDocumentChanges; docbody-only
 * batches don't reload — bodies never change the tree).
 *
 * The returned object is memoized on the loaded tree, so hosts can hang
 * provider values and effects off it without re-render storms.
 */
export function useDocumentPath(documentId, scope) {
  const treeVersion = useDocumentsUiStore((s) => s.treeVersion);
  const [tree, setTree] = useState(null); // null until first load resolves
  const scopeType =
    scope?.type === 'team' && scope.teamId != null ? 'team' : scope?.type === 'device' ? 'device' : 'personal';
  const teamId = scopeType === 'team' ? scope.teamId : null;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const load = useCallback(() => {
    let cancelled = false;
    // The user's own documents can sit in the cloud or on this device (same
    // ids either way), so links resolve across both; team trees stay separate.
    Promise.all(ownDocumentScopes(scopeRef.current).map((s) => listTree(s)))
      .then((trees) => trees.flat())
      .then((nodes) => {
        if (!cancelled) setTree(nodes);
      })
      .catch((err) => {
        // Keep whatever we had — resolve() degrades to cached titles rather
        // than flashing every chip into the deleted state on a read error.
        console.warn('[documents] breadcrumb tree load failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Initial load + reload on every tree bump (scope change reloads too).
  useEffect(() => load(), [load, scopeType, teamId, treeVersion]);

  // WS fast path: a live tree-affecting record for this exact scope reloads
  // immediately, without waiting for the engine write + treeVersion bump
  // (mirrors DocumentsSidebar's filter).
  useEffect(() => {
    const myScopeKey = recordsContext(scopeRef.current).workspaceId;
    return subscribeDocumentChanges((scopeKey, records) => {
      if (scopeKey !== myScopeKey) return;
      const treeAffecting = records.some(
        (r) => r.record_type === 'folder' || r.record_type === 'document'
      );
      if (treeAffecting) load();
    });
  }, [load, scopeType, teamId]);

  return useMemo(() => {
    const byId = tree ? new Map(tree.map((n) => [n.id, n])) : null;
    const node = byId ? byId.get(documentId) : null;
    return {
      loaded: tree != null,
      node: node || null,
      ancestors: node && byId ? documentAncestors(node, byId) : [],
      resolve: (docId) => {
        if (!byId) return null;
        const target = byId.get(docId);
        // Only documents are pages — a folder id (impossible through normal
        // chip flows) reads as not-a-page rather than leaking a folder name.
        if (target && target.type === 'document') {
          return { exists: true, title: target.name || '' };
        }
        return { exists: false, title: '' };
      },
    };
  }, [tree, documentId]);
}
